import { useCookies } from 'react-cookie';
import { useGoogleAnalytics } from './useGoogleAnalytics';
import env from '../Env';

const DAYS_UNTIL_EXPIRY_ACCEPT = 365;
const DAYS_UNTIL_EXPIRY_DECLINE = 90;

/**
 * Shared cookie consent logic used by both the CookieBar and the Navbar's
 * cookie settings entry point, so the "current" analytics choice and the
 * accept/decline behaviour stay in sync wherever they're triggered from.
 * 
 * KNOWN ISSUE ON LANDING PAGE FOR DEV/UAT is hosted on the raw *.azurewebsites.net
  // domain rather than a real *.dev/uat.earthware.co.uk subdomain, so this configured
  // domain doesn't match the landing page's host and the browser silently drops the
  // Set-Cookie call there - no cookie gets set at all on that landing page. This doesn't
  // affect prod, where the landing page and country sites are both true subdomains of
  // flexterity.com. Fixing dev/uat needs a custom domain binding for the landing app, not
  // a code change
 */
export const useCookieConsent = (gaAccount: string) => {
  const { setGaCode } = useGoogleAnalytics();
  const siteCookies = ['_ga', `_ga_${gaAccount?.split('-').pop() || ''}`];
  const [cookies, setCookie, removeCookie] = useCookies([...siteCookies, 'acceptCookie']);
  // Falls back to the current host when no shared domain is configured (e.g. localhost),
  // matching the host-only domain the cookies would otherwise be set/removed with.
  const cookieDomain = env.cookieDomain || document.domain;

  const analyticsEnabled = cookies.acceptCookie?.toString() === 'true';

  // Clears a cookie at every domain level from the full host up (host-only, then each parent
  // domain e.g. ".dev.earthware.co.uk", ".earthware.co.uk"). This catches stale copies left
  // behind by a previous cookieDomain config (e.g. before this env was introduced/changed),
  // not just the currently configured one - browsers silently ignore invalid/absent domains.
  const removeAcrossDomains = (name: string) => {
    removeCookie(name);
    const labels = document.domain.split('.');
    for (let i = 0; i < labels.length - 1; i++) {
      removeCookie(name, { domain: `.${labels.slice(i).join('.')}` });
    }
  };

  const handleAccept = () => {
    removeAcrossDomains('acceptCookie');
    const expires = new Date();
    expires.setTime(expires.getTime() + DAYS_UNTIL_EXPIRY_ACCEPT * 24 * 60 * 60 * 1000);
    setCookie('acceptCookie', 'true', { expires, domain: cookieDomain });
    setGaCode(gaAccount);
  };

  const handleDecline = () => {
    siteCookies.forEach(removeAcrossDomains);
    removeAcrossDomains('acceptCookie');
    const expires = new Date();
    expires.setTime(expires.getTime() + DAYS_UNTIL_EXPIRY_DECLINE * 24 * 60 * 60 * 1000);
    setCookie('acceptCookie', 'false', { expires, domain: cookieDomain });
  };

  return { analyticsEnabled, handleAccept, handleDecline };
};
